import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Check, Plus, Gift, Sparkles } from "lucide-react";

export default function BuildYourOwnBox() {
  const nav = useNavigate();
  const { setCustomBox } = useCart();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState("");

  useEffect(() => { api.get("/byob-items").then((r) => setItems(r.data)); }, []);

  const toggle = (item) => {
    setSelected((prev) => prev.find((i) => i.id === item.id)
      ? prev.filter((i) => i.id !== item.id)
      : [...prev, item]);
  };
  const isSel = (id) => selected.some((i) => i.id === id);
  const total = selected.reduce((s, i) => s + i.price, 0);

  const addBox = () => {
    if (selected.length < 2) { toast.error("Please choose at least 2 items for your box."); return; }
    setCustomBox({ items: selected.map((i) => ({ id: i.id, name: i.name, price: i.price })), gift_note: note });
    toast.success("Your custom box is in the cart 💛");
    nav("/cart");
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
      <div className="max-w-2xl">
        <span className="text-xs tracking-[0.25em] uppercase text-[#8A8175] font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#D9CBA0]" /> Make it personal</span>
        <h1 className="font-heading text-5xl sm:text-6xl text-[#3A3632] mt-3">Build your own box.</h1>
        <p className="text-lg text-[#6B655F] mt-4 leading-relaxed">Choose every item, add a personal note, and we'll wrap it beautifully — finished with your signature Hope Card.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-12">
        <div className="lg:col-span-2 grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          {items.map((item, i) => (
            <button key={item.id} onClick={() => toggle(item)} data-testid={`byob-item-${item.id}`}
              className={`text-left bg-white rounded-xl border-2 overflow-hidden transition-all duration-300 gh-fade-up ${isSel(item.id) ? "border-[#8E9A86] shadow-lg" : "border-[#E5DFD5] hover:border-[#D1CCC5]"}`}
              style={{ animationDelay: `${i * 40}ms` }}>
              <div className="relative aspect-square bg-[#F2EFEB]">
                <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                {isSel(item.id) && <div className="absolute inset-0 bg-[#8E9A86]/20 flex items-center justify-center"><span className="h-9 w-9 rounded-full bg-[#8E9A86] flex items-center justify-center"><Check className="h-5 w-5 text-white" /></span></div>}
              </div>
              <div className="p-4">
                <div className="text-[10px] tracking-[0.15em] uppercase text-[#8A8175]">{item.category}</div>
                <div className="font-heading text-lg text-[#3A3632] leading-tight mt-1">{item.name}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[#8E9A86] font-semibold">{money(item.price)}</span>
                  {!isSel(item.id) && <Plus className="h-4 w-4 text-[#8A8175]" />}
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="lg:sticky lg:top-28 h-fit bg-[#F2EFEB] rounded-2xl border border-[#E5DFD5] p-7">
          <h3 className="font-heading text-2xl text-[#3A3632] flex items-center gap-2"><Gift className="h-5 w-5 text-[#8E9A86]" /> Your Box</h3>
          <div className="mt-4 space-y-2 min-h-[60px]" data-testid="byob-selected-list">
            {selected.length === 0 ? (
              <p className="text-sm text-[#8A8175] italic">No items yet — tap to add the things that will make them smile.</p>
            ) : selected.map((i) => (
              <div key={i.id} className="flex justify-between text-sm">
                <span className="text-[#3A3632]">{i.name}</span>
                <span className="text-[#6B655F]">{money(i.price)}</span>
              </div>
            ))}
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="byob-note"
            placeholder="Add a personal note for the Hope Card…" rows={3}
            className="w-full mt-4 rounded-lg border border-[#D1CCC5] bg-white p-3 text-sm focus:ring-2 focus:ring-[#8E9A86] focus:border-[#8E9A86] outline-none resize-none" />
          <div className="border-t border-[#D1CCC5] mt-4 pt-4 flex justify-between items-center">
            <span className="text-[#6B655F]">Total</span>
            <span data-testid="byob-total" className="font-heading text-3xl text-[#8E9A86]">{money(total)}</span>
          </div>
          <button onClick={addBox} data-testid="byob-add-btn" className="w-full mt-4 bg-[#8E9A86] text-white rounded-full px-6 py-3.5 font-medium hover:bg-[#7A8772] transition-colors">
            Add Box to Cart
          </button>
          <p className="text-xs text-[#8A8175] text-center mt-3">Choose at least 2 items.</p>
        </div>
      </div>
    </div>
  );
}
